import {
  getActiveBonusPct,
  pickPointsPurchasePacksForShortfall,
  type PointsPurchasePackSelection,
} from './pickPointsPurchasePacksForShortfall'

export interface MaldivesRewardsPayTierPricing {
  /** Hotel package cash per person. */
  hotelPP: number
  /** Reward flight cash co-payment per person. */
  coPaymentPP: number
  /** Total cash per person on the rewards path (hotel + co-payment). */
  cashPP: number
  ptsPP: number
  fullPP: number
}

export interface MaldivesRewardsPayCalculation {
  usedPP: number
  shortfallPP: number
  hasEnough: boolean
  topUp?: PointsPurchasePackSelection
  effectivePP: number
  savings: number
  savingsPct: number
  isPayInFullCheaper: boolean
  leftoverPP: number
}

export function computeMaldivesRewardsPayCalculation(args: {
  tier: MaldivesRewardsPayTierPricing
  balancePP: number
  offers: ReadonlyArray<App.LoyaltyPurchasePointsOffer>
}): MaldivesRewardsPayCalculation {
  const { tier, balancePP, offers } = args
  const usedPP = Math.min(balancePP, tier.ptsPP)
  const shortfallPP = Math.max(0, tier.ptsPP - balancePP)

  if (shortfallPP === 0) {
    const effectivePP = tier.cashPP
    const savings = tier.fullPP - effectivePP
    const savingsPct = tier.fullPP > 0 ? Math.round((savings / tier.fullPP) * 100) : 0

    return {
      usedPP,
      shortfallPP,
      hasEnough: true,
      effectivePP,
      savings,
      savingsPct,
      isPayInFullCheaper: effectivePP > tier.fullPP,
      leftoverPP: Math.max(0, balancePP - tier.ptsPP),
    }
  }

  const topUp = pickPointsPurchasePacksForShortfall(offers, shortfallPP)
  const packCost = topUp?.packCost ?? 0
  const effectivePP = tier.cashPP + packCost
  const savings = tier.fullPP - effectivePP
  const savingsPct = tier.fullPP > 0 ? Math.round((savings / tier.fullPP) * 100) : 0

  return {
    usedPP,
    shortfallPP,
    hasEnough: false,
    topUp,
    effectivePP,
    savings,
    savingsPct,
    isPayInFullCheaper: effectivePP > tier.fullPP,
    leftoverPP: 0,
  }
}

export { getActiveBonusPct }

export function getVisibleMaldivesRewardsPayCalculatorCabins<
  TCabin extends string,
>(args: {
  tierPricingByCabin: Partial<Record<TCabin, MaldivesRewardsPayTierPricing>>
  cabins: ReadonlyArray<TCabin>
  balancePP: number
  offers: ReadonlyArray<App.LoyaltyPurchasePointsOffer>
  applyBalance: boolean
}): Array<TCabin> {
  const { tierPricingByCabin, cabins, balancePP, offers, applyBalance } = args

  return cabins.filter((cabin) => {
    const tier = tierPricingByCabin[cabin]
    if (!tier) {
      return false
    }

    if (!applyBalance) {
      return true
    }

    return !computeMaldivesRewardsPayCalculation({ tier, balancePP, offers }).isPayInFullCheaper
  })
}
